import React, { useEffect, useState } from "react";
import { TextInput } from "../../components/form/inputs";
import ToggleBtn from "../../components/button/ToggleBtn";
import { useNavigate, useParams } from "react-router-dom";
import PrimaryBtn from "../../components/button/PrimaryBtn";
import { GoogleMap, Marker, Autocomplete } from "@react-google-maps/api";
import { useDispatch, useSelector } from "react-redux";
import { addOrupdateAddress } from "../../redux/thunks/customerAddThunk";
import toast from "react-hot-toast";
import DecorativeSquares from "../../components/ui/DecorativeSquares";
import { FaArrowLeft } from "react-icons/fa";
import { decryptId, encryptId } from "../../utils/urlCrypto";
// HELPER FUNCTION:
const extractAddress = (place) => {
  const address = {
    country: "",
    city: "",
    province: "",
    pincode: "",
    fullAddress: place.formatted_address || "",
    addressLine2: "",
  };

  let sublocality = "";
  let locality = "";
  let route = "";

  place.address_components.forEach((component) => {
    const types = component.types;

    if (types.includes("country")) address.country = component.long_name;
    if (types.includes("administrative_area_level_1"))
      address.province = component.long_name;
    if (types.includes("locality")) {
      address.city = component.long_name;
      locality = component.long_name;
    }
    if (types.includes("sublocality") || types.includes("sublocality_level_1"))
      sublocality = component.long_name;
    if (types.includes("route")) route = component.long_name;
    if (types.includes("postal_code")) address.pincode = component.long_name;
  });

  // 🔥 Short nearby address (priority wise)
  address.addressLine2 =
    sublocality || route || locality || address.province || "";

  return address;
};
const AddCustomerAddress = () => {
  const { customer_id: paramCustomerId } = useParams();
  const customer_id = decryptId(paramCustomerId);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isFetchingAddress, setIsFetchingAddress] = useState(false);
  const { isAddressLoading } = useSelector((state) => state.customerAddress);
  const [address, setAddress] = useState({
    fullAddress: "",
    addressLine2: "",
    country: "",
    city: "",
    state: "",
    pincode: "",
    lat: null,
    lng: null,
  });

  const mapContainerStyle = {
    width: "100%",
    height: "300px",
  };

  const center = {
    lat: 20.5937,
    lng: 78.9629,
  };

  const [mapCenter, setMapCenter] = useState(center);
  const [markerPosition, setMarkerPosition] = useState(null);
  const autocompleteRef = React.useRef(null);

  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (!place.geometry) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    const extracted = extractAddress(place);

    setAddress((prev) => ({
      ...prev,
      ...extracted,
      lat,
      lng,
    }));

    setMapCenter({ lat, lng });
    setMarkerPosition({ lat, lng });
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          // ✅ Only update map & marker
          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });
        },
        (error) => {
          console.log("Location permission denied", error);
        },
      );
    }
  }, []);

  const handleMapClick = (event) => {
    setIsFetchingAddress(true);
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    setMarkerPosition({ lat, lng });
    setMapCenter({ lat, lng });

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        const place = results[0];

        const extracted = extractAddress(place);

        setAddress((prev) => ({
          ...prev,
          ...extracted,
          lat,
          lng,
        }));
      } else {
        toast.error("Unable to fetch address from map");
      }

      setTimeout(() => {
        setIsFetchingAddress(false);
      }, 400);
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setAddress((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const onAddressSave = async () => {
    try {
      const payload = {
        customer_id: customer_id,
        address_1: address.fullAddress,
        address_2: address.addressLine2,
        country: address.country,
        city: address.city,
        province: address.state,
        pin_code: address.pincode,
        latitude: address.lat,
        longitude: address.lng,
      };
      // console.log("The payload data is :", payload);

      const res = await dispatch(addOrupdateAddress(payload)).unwrap();

      if (res.success) {
        toast.success(res.message || "Address Added Successfully");
        navigate(`/address/${encryptId(customer_id)}`);
      } else {
        toast.error(res.message || "Address Failed");
      }
    } catch (err) {
      toast.error(err?.message || err);
    }
  };

  return (
    <>
      <section className="bg-white mx-auto max-w-7xl p-3 md:p-8 border border-gray-100 rounded-md ">
        <DecorativeSquares />
        <div className="sticky top-2 z-10 bg-white/80 backdrop-blur-md rounded-2xl shadow-md px-4 py-3 flex items-center justify-between mb-6">
          <button
            onClick={handleGoBack}
            className="flex items-center p-2 rounded-full hover:bg-gray-100"
          >
            <FaArrowLeft size={20} />
          </button>

          <h1 className=" text-xl mx-auto font-semibold text-gray-900">
            Add Customer address
          </h1>
        </div>
        <div className="max-w-7xl mx-auto  px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="w-full mt-4">
            <p className="text-[#191919] font-medium">
              Delivery Address <span className="text-red-500 text-sm">*</span>
            </p>

            <Autocomplete
              onLoad={(ref) => (autocompleteRef.current = ref)}
              onPlaceChanged={onPlaceChanged}
            >
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search location"
                  name="fullAddress"
                  value={address.fullAddress}
                  onChange={handleChange}
                  disabled={isFetchingAddress}
                  className="w-full px-4 py-2 mb-3 border rounded-lg outline-none pr-10"
                />

                {isFetchingAddress && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-black rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
            </Autocomplete>
          </div>
          {/* ================= Address Form ================= */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
            <TextInput
              label="Address Line 2"
              required={true}
              name="addressLine2"
              value={address.addressLine2}
              onChange={handleChange}
            />

            <TextInput
              label="Country"
              required={true}
              name="country"
              value={address.country}
              onChange={handleChange}
            />

            <TextInput
              label="City"
              required={true}
              name="city"
              value={address.city}
              onChange={handleChange}
            />

            <TextInput
              label="Province / State"
              required={true}
              name="state"
              value={address.state}
              onChange={handleChange}
            />

            <TextInput
              label="Pin Code"
              required={true}
              name="pincode"
              value={address.pincode}
              onChange={handleChange}
            />
          </div>

          {/* ================= Google Map ================= */}
          <div className="space-y-2">
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              zoom={15}
              center={mapCenter}
              onClick={handleMapClick}
            >
              {markerPosition && <Marker position={markerPosition} />}
            </GoogleMap>
          </div>

          <div className="flex justify-center md:justify-end">
            <PrimaryBtn
              btnName="Add"
              clickEvent={onAddressSave}
              extraClass="w-full   px-10"
              isLoading={isAddressLoading}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default AddCustomerAddress;
